import { ragLabel } from "@/utils/districtDetailDerived";

const titleCase = (name) => {
  if (!name) return "";
  const s = String(name);
  return s.charAt(0) + s.slice(1).toLowerCase();
};

const pct = (v, digits = 1) => (typeof v === "number" ? `${v.toFixed(digits)}%` : "—");

export const DISTRICT_HERO_WARNING_CONFIG = {
  blocks_at_risk: {
    id: "blocks_at_risk",
    title: "Blocks At Risk — Drill-Down",
    summary: (district, targetBlocks) =>
      `${targetBlocks.length} block${targetBlocks.length !== 1 ? "s" : ""} below excellent OSH band · district OSH ${(district?.shi_score ?? 0).toFixed(1)}`,
    selectBlocks: (blocks) =>
      [...blocks]
        .filter((b) => b.rag_status !== "green")
        .sort((a, b) => (a.shi_score ?? 0) - (b.shi_score ?? 0)),
    blockReason: (block) => {
      const parts = [];
      const osh = block.shi_score ?? 0;
      const aadhaar = block.aadhaar_percentage ?? 0;
      const apaar = block.apaar_percentage ?? 0;
      if (block.rag_status === "red") parts.push("Block classified as critical on composite OSH.");
      else parts.push("Block classified as at-risk on composite OSH.");
      if (osh < 85) parts.push(`OSH score ${osh.toFixed(1)} is below the district excellence band (85+).`);
      if (apaar < 85 && aadhaar - apaar > 8) {
        parts.push(`APAAR (${pct(apaar)}) trails Aadhaar (${pct(aadhaar)}) by ${(aadhaar - apaar).toFixed(1)} points.`);
      }
      if (aadhaar < 90) parts.push(`Aadhaar compliance at ${pct(aadhaar)} needs targeted closure.`);
      return parts.join(" ");
    },
    blockResolution: (block) => {
      const apaarGap = (block.aadhaar_percentage ?? 0) - (block.apaar_percentage ?? 0);
      const actions = [
        `Assign a block officer to review ${titleCase(block.block_name)} weekly for 30 days.`,
        "Validate school-level OSH drivers: identity gaps, PTR outliers, and data-entry exceptions.",
      ];
      if (apaarGap > 10) actions.push("Run APAAR generation camps in schools with the largest Aadhaar–APAAR gap.");
      if ((block.shi_score ?? 0) < 80) actions.push("Escalate critical schools (OSH < 70) for same-week corrective follow-up.");
      return actions.join(" ");
    },
    selectSchools: (schools, block) => {
      const flagged = schools.filter(
        (s) => s.rag_status === "red" || (s.shi_score ?? 100) < 70 || (s.aadhaar_percentage ?? 100) < 80,
      );
      const pool = flagged.length > 0 ? flagged : schools;
      return [...pool].sort((a, b) => (a.shi_score ?? 0) - (b.shi_score ?? 0)).slice(0, 8);
    },
    schoolReason: (school) => {
      const parts = [];
      if (school.rag_status === "red") parts.push("Critical OSH status.");
      if ((school.shi_score ?? 100) < 70) parts.push(`Low OSH (${(school.shi_score ?? 0).toFixed(1)}).`);
      if ((school.aadhaar_percentage ?? 100) < 80) parts.push(`Weak Aadhaar (${pct(school.aadhaar_percentage)}).`);
      if ((school.apaar_percentage ?? 100) < 70) parts.push(`Low APAAR (${pct(school.apaar_percentage)}).`);
      if ((school.total_teachers ?? 0) === 0 && (school.total_students ?? 0) > 0) parts.push("Zero teachers on record.");
      return parts.join(" ") || "Among lowest OSH schools in the block.";
    },
    schoolAction: () => "Weekly block review + school exception closure",
  },
  apaar_lower_blocks: {
    id: "apaar_lower_blocks",
    title: "APAAR Coverage — Lower Blocks",
    summary: (district, targetBlocks) =>
      `District APAAR ${pct(district?.apaar_percentage ?? 0)} · ${targetBlocks.length} block${targetBlocks.length !== 1 ? "s" : ""} below district average`,
    selectBlocks: (blocks, district) => {
      const avg = district?.apaar_percentage ?? 0;
      const sorted = [...blocks].sort((a, b) => (a.apaar_percentage ?? 0) - (b.apaar_percentage ?? 0));
      const below = sorted.filter((b) => (b.apaar_percentage ?? 0) < avg - 0.5);
      return (below.length > 0 ? below : sorted.slice(0, 4)).slice(0, 6);
    },
    blockReason: (block, district) => {
      const apaar = block.apaar_percentage ?? 0;
      const aadhaar = block.aadhaar_percentage ?? 0;
      const gap = aadhaar - apaar;
      const vsDistrict = (district?.apaar_percentage ?? 0) - apaar;
      return `APAAR at ${pct(apaar)} is ${vsDistrict > 0 ? `${vsDistrict.toFixed(1)} points below` : "near"} the district average. Aadhaar–APAAR gap of ${gap.toFixed(1)} points indicates incomplete student identity completion after Aadhaar enrolment.`;
    },
    blockResolution: (block) =>
      `Launch a 30-day APAAR closure sprint in ${titleCase(block.block_name)}: prioritise schools with Aadhaar > 90% but APAAR < 75%, coordinate data-entry teams, and track daily generation against enrolled students.`,
    selectSchools: (schools, block) => {
      const blockApaar = block.apaar_percentage ?? 0;
      const flagged = schools.filter((s) => {
        const apaar = s.apaar_percentage ?? 0;
        const aadhaar = s.aadhaar_percentage ?? 0;
        return apaar < blockApaar - 2 || aadhaar - apaar > 12 || apaar < 70;
      });
      const pool = flagged.length > 0 ? flagged : schools;
      return [...pool].sort((a, b) => (a.apaar_percentage ?? 0) - (b.apaar_percentage ?? 0)).slice(0, 8);
    },
    schoolReason: (school) => {
      const apaar = school.apaar_percentage ?? 0;
      const aadhaar = school.aadhaar_percentage ?? 0;
      const gap = aadhaar - apaar;
      if (gap > 15) return `Aadhaar–APAAR gap of ${gap.toFixed(1)} points — identity completion pending.`;
      if (apaar < 60) return `APAAR coverage critically low at ${pct(apaar)}.`;
      return `APAAR at ${pct(apaar)} is below block average.`;
    },
    schoolAction: () => "APAAR generation camp + student-wise validation",
  },
};

export const getDistrictHeroWarningBlocks = (warningId, blocks, district) => {
  const config = DISTRICT_HERO_WARNING_CONFIG[warningId];
  if (!config) return [];
  return config.selectBlocks(blocks, district);
};

export const blockWarningStatusLabel = (block) => ragLabel(block.rag_status);
